import React, { createContext, useContext, useMemo } from 'react';

const EMPTY_SCOPE: Readonly<Record<string, unknown>> = Object.freeze({});

const PublishedScopeContext = createContext<
  Readonly<Record<string, unknown>> | undefined
>(undefined);

export interface PublishedScopeProviderProps {
  /**
   * Keys this provider publishes. Overlay onto the parent bag: a key
   * whose value is `null` replaces the parent and does not fall through.
   * A key omitted here stays unpublished at this level.
   */
  readonly scope: Readonly<Record<string, unknown>>;
  readonly children: React.ReactNode;
}

/**
 * Publishes a named bag for `{{scope.*}}`. Nested providers overlay.
 * The nearest ancestor wins, including an explicit `null`.
 */
export const PublishedScopeProvider: React.FC<PublishedScopeProviderProps> = ({
  scope,
  children,
}) => {
  const parent = useContext(PublishedScopeContext);
  const merged = useMemo(
    () => ({ ...(parent ?? EMPTY_SCOPE), ...scope }),
    [parent, scope],
  );

  return (
    <PublishedScopeContext.Provider value={merged}>
      {children}
    </PublishedScopeContext.Provider>
  );
};

/**
 * Merged bag from the nearest provider chain, or an empty bag when
 * nothing has published. An empty bag makes every key unpublished.
 */
export function usePublishedScope(): Readonly<Record<string, unknown>> {
  return useContext(PublishedScopeContext) ?? EMPTY_SCOPE;
}

/** The published bag, or `undefined` when no provider is mounted. */
export function useOptionalPublishedScope():
  | Readonly<Record<string, unknown>>
  | undefined {
  return useContext(PublishedScopeContext);
}
